import { TMableEvent } from './base_mable_event';
import { TCartData, TMableProduct } from './extended_mable_event';

export enum EMableEventName {
  /** Event name for when the checkout process is started */
  CHECKOUT_STARTED = 'Checkout Started',
  /** Event name for when a collection is viewed */
  COLLECTION_VIEWED = 'Collection Viewed',
  /** Event name for custom events */
  CUSTOM_EVENT = 'Custom Event',
  /** Event name for lead generation */
  LEAD = 'Lead',
  /** Event name for when an order is completed */
  ORDER_COMPLETED = 'Order Completed',
  /** Event name for when a page is viewed */
  PAGE_VIEWED = 'Page Viewed',
  /** Event name for when payment information is added */
  PAYMENT_INFO_ADDED = 'Payment Info Entered',
  /** Event name for when a post purchase survey is completed */
  POST_PURCHASE_SURVEY = 'Post Purchase Survey',
  /** Event name for when a product is added to the cart */
  PRODUCT_ADDED = 'Product Added',
  /** Event name for when a product is viewed */
  PRODUCT_VIEWED = 'Product Viewed',
}

/**
 * Custom event names for Mable
 */
export enum EMableCustomEventName {
  AllCustomerPurchase = 'AllCustomerPurchase',
  CUSTOM_EVENT = 'CUSTOM_EVENT',
  NewCustomerPurchase = 'NewCustomerPurchase',
  ReturningCustomerPurchase = 'ReturningCustomerPurchase',
}

export type ProductViewedEvent = TMableEvent & {
  /** Event name */
  en: EMableEventName.PRODUCT_VIEWED;
  /** Product viewed */
  esd: {
    product: TMableProduct;
  };
};

export type CollectionViewedEvent = TMableEvent & {
  /** Event name */
  en: EMableEventName.COLLECTION_VIEWED;
  /** Product viewed in the collection */
  esd: {
    product: TMableProduct;
  };
};

export type ProductAddedEvent = TMableEvent & {
  /** Event name */
  en: EMableEventName.PRODUCT_ADDED;
  /** Event specific data */
  esd: {
    /** Amount added */
    amount: number;
    /** Cart data */
    cart: TCartData;
    /** Product added to the cart */
    product: TMableProduct;
  };
};

export type MableProductEvent =
  | CollectionViewedEvent
  | ProductAddedEvent
  | ProductViewedEvent;

export type MableCheckoutEvent =
  | CheckoutStartedEvent
  | OrderCompletedEvent
  | PaymentInfoAddedEvent;

export type CheckoutStartedEvent = TMableEvent & {
  /** Event name */
  en: EMableEventName.CHECKOUT_STARTED;
  /** Event specific data */
  esd: {
    /** Cart data */
    cart: TCartData;
  };
};

export type PaymentInfoAddedEvent = TMableEvent & {
  /** Event name */
  en: EMableEventName.PAYMENT_INFO_ADDED;
  /** Event specific data */
  esd: {
    /** Cart data */
    cart: TCartData;
    /** Payment method */
    pay_methods: string[];
  };
};

export type OrderCompletedEvent = TMableEvent & {
  /** Event name */
  en: EMableEventName.ORDER_COMPLETED;
  /** Event specific data */
  esd: {
    /** Cart data */
    cart: TCartData;
    /** Order ID */
    oid: string;
    /** Order number */
    onb: string;
    /** Tags */
    tags: string[];
  };
};

export type LeadEvent = TMableEvent & {
  /** Event name for lead generation */
  en: EMableEventName.LEAD;
};

export type PageViewedEvent = TMableEvent & {
  /** Event name for page viewed */
  en: EMableEventName.PAGE_VIEWED;
};

export type CustomMableEvent = TMableEvent & {
  /** Event name for custom event */
  en: EMableEventName.CUSTOM_EVENT;
  /** Event specific data */
  esd: {
    /** Cart data (optional) */
    cart?: TCartData;
    /** Custom event name */
    cen: string;
    /** Product data (optional) */
    product?: TMableProduct;
  };
};

/**
 * Events that support conversion mapping for Google Ads destinations
 * These are the only events that need conversion resource mapping
 */
export const CONVERSION_MAPPING_SUPPORTED_EVENTS = new Set<EMableEventName>([
  EMableEventName.LEAD,
  EMableEventName.ORDER_COMPLETED,
  EMableEventName.CHECKOUT_STARTED,
  EMableEventName.PAGE_VIEWED,
  EMableEventName.PRODUCT_ADDED,
  EMableEventName.PRODUCT_VIEWED,
  EMableEventName.COLLECTION_VIEWED,
  EMableEventName.PAYMENT_INFO_ADDED,
]);
